// config after beeing process by the ConfigService
import type { Menu, MenuItemType, ThemeType } from '@evc/web-components';

import type { AuthConfig } from '../../clients/core/auth/auth.type';
import type { AppIdentity } from '../types/app-identity.type';
import type { ApplicationType } from '../types/user-menu.type';
import type { AvailableLanguages, Env, PlatformEnv } from './env.type';

export type ReservedApps =
  | 'root'
  | 'public'
  | 'logout'
  | 'homepage'
  | 'user'
  | 'admin'
  | 'organization'
  | 'marketplace'
  | 'licenses'
  | 'equipment'
  | 'drive';

export type ApplicationURIs = Record<ReservedApps, string>;

export const CORE_API_ENDPOINTS = {
  signupsignin: 'v1/idm/signupsignin',
  user: {
    me: 'v1/user/me',
    applications: 'v1/user/me/apps',
    permissions: 'v1/user/me/permissions',
    profilePicture: 'v1/user/me/profile-picture',
    floatingLicense: 'v1/user/me/apps/floating-license',
  },
  organization: {
    create: 'v1/organization/create',
    list: 'v1/organization/user-organizations',
    current: 'v1/organization/current',
    exists: 'v1/organization/exists',
    roles: 'v1/organization/roles',
    role: 'v1/organization/role',
    update: {
      information: 'v1/organization/update',
      logo: 'v1/organization/update/logo',
    },
    users: {
      list: 'v1/organization/users',
      count: 'v1/organization/users/count',
      delete: 'v1/organization/user/{{userId}}',
      changeRole: 'v1/organization/user/{{userId}}/role/{{roleId}}',
    },
    invitations: {
      status: 'v1/organization/invitation/status',
      send: 'v1/organization/invitation/send',
      accept: 'v1/organization/invitation/accept',
      renew: 'v1/organization/invitation/renew/{{invitationId}}',
      revoke: 'v1/organization/invitation/{{invitationId}}',
      list: 'v1/organization/invitations',
    },
    link: {
      invite: 'v1/organization/link/invite',
      list: 'v1/organization/link',
      accept: 'v1/organization/link/accept',
      remove: 'v1/organization/link',
    },
  },
  licensing: {
    unlicensedProducts: 'unlicensed',
    saasUser: 'v1/licensing/saas/user',
    saasOrganization: 'v1/licensing/saas/organization',
    assignUser: 'v1/licensing/saas/{{licenseId}}/assign-user',
    assignDevice: 'v1/licensing/saas/{{licenseId}}/assign-device',
    unassign: 'v1/licensing/saas/{{licenseId}}/assignment/{{assignmentId}}',
    share: 'v1/licensing/saas/{{licenseId}}/share',
    unshare: 'v1/licensing/saas/{{licenseId}}/share/{{shareId}}',
    claimLicense: 'v1/licensing/saas/claim',
  },
} as const;
export type CoreApiEndpoints = typeof CORE_API_ENDPOINTS;

export const STORAGE_API_ENDPOINTS = {
  ai: {
    jobs: 'v1/ai/jobs',
    files: 'v1/ai/files/{{fileId}}/jobs',
  },
  files: {
    browse: 'v1/files/browse',
    file: 'v1/files/{{fileId}}',
    move: 'v1/files/move',
    rename: 'v1/files/rename',
    directory: 'v1/files/directory',
    copy: 'v1/files/copy',
    trash: {
      restore: 'v1/files/trash/{{fileId}}',
      permanent: 'v1/files/trash/permanent/{{fileId}}',
      list: 'v1/files/trash',
      empty: 'v1/files/trash/empty',
    },
  },
  transfer: {
    upload: {
      init: 'v1/transfer/init-upload',
      id: 'v1/transfer/upload/{{uploadId}}',
      complete: 'v1/transfer/upload/complete/{{uploadId}}',
    },
    download: {
      signedUrl: 'v1/transfer/download/signed-url',
    },
  },
} as const;
export type StorageApiEndpoints = typeof STORAGE_API_ENDPOINTS;

export const DEVICE_API_ENDPOINTS = {
  device: {
    list: 'v1/device',
    registered: 'v1/device/registered',
    manuals: 'v1/device/{{deviceModelId}}/manuals',
    register: 'v1/device/register',
    unregister: 'v1/device/unregister/{{registrationId}}',
    rename: 'v1/device/nickname/{{registrationId}}',
    renew: 'v1/device/renew-activation-code',
    certificates: 'v1/device/{{deviceModelId}}/calibration-certificates/{{serialNumber}}',
  },
  firmware: {
    list: 'v1/devicefirmwares/{{deviceModelId}}',
    signedUrl: 'v1/devicefirmwares/{{deviceModelId}}/firmware/{{firmwareId}}/signed-url',
  },
  meeting: {
    history: 'v1/meeting',
  },
} as const;
export type DeviceApiEndpoints = typeof DEVICE_API_ENDPOINTS;

export type DefaultDomain = 'CORE' | 'STORAGE' | 'DEVICE';

export type ApiConfig = {
  uris: Record<string, string>;
  endpoints: CoreApiEndpoints & StorageApiEndpoints & DeviceApiEndpoints;
};

export type I18nConfig = {
  locales: string[];
  storageKey: string;
  /** may support many languages defined by env LOCALES */
  languages: string[];
  /** private because platform does not support other languages internaly (auth) */
  platformLanguages: AvailableLanguages[];
  lang?: string;
  defaultLang?: string;
};

export type MenuSettings = Menu & {
  type: 'settings';
};
export type MenuHelp = Menu & {
  type: 'help';
};

// config after beeing process by the ConfigService
/** @internal */
export type PlatformConfigConnect = {
  // require USE_CONNECT_PLATFORM to be true
  useConnect: boolean;
  // require AUTH_CLIENT_ID
  auth?: AuthConfig;
  // require PROTECTED_URIS.CORE to be defined
  api?: ApiConfig;
};

export type PlatformConfig = {
  env: Env;
  app: AppIdentity;
  uris: ApplicationURIs;
  applications?: ApplicationType[];
  theme: ThemeType;
  topTabs?: MenuItemType[];
  isStandaloneApplication?: boolean;
  menus: {
    settings: Menu;
    help: Menu;
    leftbar: Menu[];
  };
  // fallback icon used for webapp without icons (apps menu)
  fallbackApplicationIcon: string;
  i18n: I18nConfig;
} & PlatformConfigConnect &
  PlatformEnv;

export type PlatformConfigOption = keyof PlatformConfig;

// @see ./env.type.ts for same config but for .env
